import type { ActionParameterValue, TimeWindow } from "@repo/api";
import type { ReviseProgramDefinitionInput } from "@repo/api/schemas";
import type { ExamDefinitionDto, ProgramDefinitionDto, ProtocolDefinitionDto } from "@repo/data-store";
import { ENVELOPE_DURATION_CAP_MINUTES, resolvePlacement } from "@repo/domain";
import { useMemo } from "react";
import { z } from "zod";
import { useAppForm } from "@/shared/forms/app-form";
import type {
  ProtocolBuilderDomainAnswer,
  ProtocolBuilderPlannedTrial,
} from "../components/builder/protocol-builder-definition";

// === Schema ===
const legacyParameterValueSchema = z.object({
  name: z.string(),
  code: z.string(),
  display: z.string(),
});

const memberIdSchema = z.string().regex(/^[a-z][a-z0-9]*-[1-9]\d*$/);
const domainParameterValueSchema = z.object({
  source: z.union([z.literal("self"), memberIdSchema]),
  parameterId: memberIdSchema,
  value: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("Quantity"),
      value: z.number(),
      unit: z.object({ code: z.string(), display: z.string() }).optional(),
    }),
    z.object({ kind: z.literal("Integer"), value: z.number().int() }),
    z.object({ kind: z.literal("Choice"), selected: z.array(memberIdSchema), freeText: z.string().optional() }),
    z.object({ kind: z.literal("Boolean"), value: z.boolean() }),
    z.object({ kind: z.literal("Text"), value: z.string() }),
  ]),
});

const actionFormSchema = z.object({
  id: z.string(),
  title: z.string(),
  qualifier: z.string().optional(),
  description: z.string(),
  definitionCanonical: z.string().optional(),
  definitionResourceType: z.literal("ActivityDefinition").optional(),
  parameterValues: z.array(z.union([legacyParameterValueSchema, domainParameterValueSchema])),
  observationLevels: z.array(z.object({ observationId: z.string(), level: z.literal("trial") })).optional(),
  plannedTrials: z.array(z.object({ id: z.string(), values: z.array(domainParameterValueSchema) })).optional(),
});

const stepFormSchema = z.object({
  offsetMinutes: z.number().int().nonnegative().multipleOf(5).max(ENVELOPE_DURATION_CAP_MINUTES),
  durationMinutes: z.number().int().positive().multipleOf(5).max(ENVELOPE_DURATION_CAP_MINUTES).optional(),
  id: z.string(),
  title: z.string(),
  actions: z.array(actionFormSchema),
});

const protocolFormSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
  definitionCanonical: z.string().optional(),
  timeWindow: z.enum(["", "early_morning", "mid_morning", "midday", "early_afternoon", "late_afternoon"]),
  /**
   * FHIR extensions carried through the form untouched (action parameter
   * values, mostly). Typed `unknown[]`, NOT `Extension[]`, on purpose:
   * TanStack Form's `DeepKeysAndValuesImpl` walks the whole value type with no
   * depth limit, and `Extension` contains `Extension[]` — recursive, with ~50
   * `value[x]` members per level. `unknown` short-circuits that walk on its
   * first branch. Readers cast at the edge; see `code-value.ts` for the same
   * problem hit from the other direction.
   */
  extension: z.custom<unknown[]>().optional(),
});

const dayFormSchema = z.object({
  id: z.string(),
  title: z.string(),
  protocols: z.array(protocolFormSchema).min(1, "Add at least one protocol"),
});

/**
 * A plan definition's OWN fields. Title, description and status are not here:
 * the definition shell owns those three on every kind, and they reach the
 * domain input through `DefinitionMeta` at save.
 */
export const planDefinitionFormSchema = z.object({
  envelopeMinutes: z.number().positive().multipleOf(5).max(ENVELOPE_DURATION_CAP_MINUTES),
  // Protocol-mode fields
  actions: z.array(actionFormSchema),
  // Protocol two-level model: steps[] take precedence over flat actions
  steps: z.array(stepFormSchema),
  exams: z.array(z.object({ canonical: z.string().min(1), title: z.string() })),
  // Program-mode fields
  days: z.array(dayFormSchema),
});

// === Types ===

export type PlanDefinitionFormValues = z.infer<typeof planDefinitionFormSchema>;
export type ActionFormValues = z.infer<typeof actionFormSchema>;
export type BuilderParameterAnswer = ActionParameterValue | ProtocolBuilderDomainAnswer;
export type StepFormValues = z.infer<typeof stepFormSchema>;
export type ProtocolFormValues = z.infer<typeof protocolFormSchema>;
/** @deprecated use ProtocolFormValues */
export type SessionFormValues = ProtocolFormValues;
export type DayFormValues = z.infer<typeof dayFormSchema>;
export type BuilderMode = "protocol" | "program";

export type PlanDefinitionDetails = ProgramDefinitionDto;

// === Defaults ===

let counter = 0;
function generateActionId(): string {
  counter++;
  return `action-${Date.now()}-${counter}-${Math.random().toString(36).slice(2, 6)}`;
}

export function createDefaultAction(): ActionFormValues {
  return {
    id: generateActionId(),
    title: "",
    description: "",
    parameterValues: [],
  };
}

export function createDefaultStep(offsetMinutes = 0): StepFormValues {
  return {
    offsetMinutes,
    id: generateActionId(),
    title: "",
    actions: [],
  };
}

export function createDefaultProtocol(timeWindow?: TimeWindow): ProtocolFormValues {
  return {
    id: generateActionId(),
    timeWindow: timeWindow ?? "",
  };
}

export function createDefaultDay(dayNumber: number): DayFormValues {
  return {
    id: generateActionId(),
    title: `Day ${dayNumber}`,
    protocols: [],
  };
}

/** The shell's half of a definition, handed to the build functions at save. */
export interface DefinitionMeta {
  title: string;
  description: string;
}

export const EMPTY_DEFAULTS: PlanDefinitionFormValues = {
  envelopeMinutes: 5,
  actions: [],
  steps: [],
  exams: [],
  days: [],
};

// === Converters: details DTO → form ===

const TIME_WINDOW_VALUES = new Set<string>([
  "early_morning",
  "mid_morning",
  "midday",
  "early_afternoon",
  "late_afternoon",
]);

function coerceTimeWindow(v: string | undefined): ProtocolFormValues["timeWindow"] {
  return v && TIME_WINDOW_VALUES.has(v) ? (v as TimeWindow) : "";
}

function programDetailsToDefaults(details: ProgramDefinitionDto): PlanDefinitionFormValues {
  const days: DayFormValues[] = details.days.map((day) => ({
    id: day.dayId,
    title: `Day ${day.dayNumber}`,
    protocols: day.sessions.map((s) => ({
      id: s.sessionId,
      definitionCanonical: s.protocolDefinitionUrl,
      timeWindow: coerceTimeWindow(s.timeWindow),
    })),
  }));

  return {
    envelopeMinutes: 5,
    actions: [],
    steps: [],
    exams: [],
    days,
  };
}

export function detailsToDefaults(details: PlanDefinitionDetails | null, mode: BuilderMode): PlanDefinitionFormValues {
  if (!details) return { ...EMPTY_DEFAULTS };
  if (mode === "program") return programDetailsToDefaults(details);
  return { ...EMPTY_DEFAULTS };
}

// === Form values → program domain input ===

export type ProgramDomainInput = {
  description?: string;
  days: ReviseProgramDefinitionInput["days"];
};

export function buildProgramDomainInput(
  meta: DefinitionMeta,
  values: PlanDefinitionFormValues,
  protocolItems: ProtocolDefinitionDto[],
): ProgramDomainInput {
  const days: ReviseProgramDefinitionInput["days"] = values.days.map((day) => ({
    ...(memberIdSchema.safeParse(day.id).success ? { id: day.id } : {}),
    sessions: day.protocols.map((p) => {
      const protocol = protocolItems.find((item) => item.definitionUrl === p.definitionCanonical);
      if (!protocol) throw new Error(`Protocol Definition '${p.definitionCanonical ?? ""}' is unavailable`);
      // Empty timeWindow falls through to schema validation (rejected).
      const timeWindow = (p.timeWindow || "mid_morning") as Exclude<ProtocolFormValues["timeWindow"], "">;
      return {
        ...(memberIdSchema.safeParse(p.id).success ? { id: p.id } : {}),
        protocol: { definitionUrl: protocol.definitionUrl },
        timeWindow,
      };
    }),
  }));

  return {
    description: meta.description || undefined,
    days,
  };
}

// === Hook ===

interface UsePlanDefinitionFormOptions {
  details: PlanDefinitionDetails | null;
  mode: BuilderMode;
  protocolItems?: ProtocolDefinitionDto[];
  /** Program-mode save — receives program domain input. */
  onProgramSave?: (input: ProgramDomainInput) => void | Promise<void>;
}

interface UsePlanDefinitionFormReturn {
  defaultValues: PlanDefinitionFormValues;
  validators: { onDynamic: typeof planDefinitionFormSchema };
  handleSubmit: (meta: DefinitionMeta, value: PlanDefinitionFormValues) => Promise<void>;
}

export function usePlanDefinitionFormData({
  details,
  mode,
  protocolItems,
  onProgramSave,
}: UsePlanDefinitionFormOptions): UsePlanDefinitionFormReturn {
  // Memoize to stabilize generated IDs across renders
  const identity = details && ("definitionId" in details ? details.definitionId : details.definitionUrl);
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally keyed on stable definition identity
  const defaultValues = useMemo(() => detailsToDefaults(details, mode), [identity, mode]);

  const handleSubmit = async (meta: DefinitionMeta, value: PlanDefinitionFormValues) => {
    if (mode === "program" && onProgramSave) {
      await onProgramSave(buildProgramDomainInput(meta, value, protocolItems ?? []));
      return;
    }
  };

  return {
    defaultValues,
    validators: { onDynamic: planDefinitionFormSchema },
    handleSubmit,
  };
}

/**
 * The builder's form, created by the kind component so it can read the values
 * at save and hand them to the Program Definition persistence adapter.
 */
export function usePlanDefinitionForm(details: PlanDefinitionDetails | null, mode: BuilderMode) {
  const identity = details && ("definitionId" in details ? details.definitionId : details.definitionUrl);
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the item id so generated action ids stay stable across renders
  const defaultValues = useMemo(() => detailsToDefaults(details, mode), [identity, mode]);
  return useAppForm({
    defaultValues,
    validators: { onDynamic: planDefinitionFormSchema },
    onSubmit: () => {},
    onSubmitInvalid() {
      const invalid = document.querySelector('[aria-invalid="true"]') as HTMLElement | null;
      invalid?.focus();
    },
  });
}

/** Domain ProtocolDefinition -> the established neutral drag/drop builder form. */
export function protocolDefinitionToBuilderDefaults(
  definition: ProtocolDefinitionDto | undefined,
  exams: readonly ExamDefinitionDto[],
): PlanDefinitionFormValues {
  // A new protocol gets its first step up front: the step builder renders from
  // steps[], so an empty one would leave nowhere to add the first occurrence.
  // It is dropped again at save if the author never fills it.
  if (!definition) return { ...EMPTY_DEFAULTS, steps: [createDefaultStep()] };
  const steps: StepFormValues[] = definition.steps.map((step, stepIndex) => ({
    id: `protocol-step-${stepIndex + 1}`,
    offsetMinutes: step.placement.offsetMinutes,
    durationMinutes: step.placement.durationMinutes,
    title: step.title ?? "",
    actions: step.occurrences.map((occurrence) => ({
      id: occurrence.occurrenceId,
      title: "",
      ...(occurrence.qualifier ? { qualifier: occurrence.qualifier } : {}),
      description: "",
      definitionCanonical: occurrence.definitionUrl,
      definitionResourceType: "ActivityDefinition" as const,
      parameterValues: occurrence.parameterValues.map(builderAnswer),
      ...(occurrence.observationLevels?.length
        ? { observationLevels: occurrence.observationLevels.map((entry) => ({ ...entry })) }
        : {}),
      ...(occurrence.plannedTrials?.length
        ? {
            plannedTrials: occurrence.plannedTrials.map((trial) => ({
              id: trial.id,
              values: trial.values.map(builderAnswer),
            })),
          }
        : {}),
    })),
  }));
  return {
    actions: [],
    steps,
    exams: definition.examDefinitionUrls.map((url) => ({
      canonical: url,
      title: exams.find((exam) => exam.definitionUrl === url)?.name ?? url,
    })),
    days: [],
    envelopeMinutes: definition.envelopeMinutes,
  };
}

function builderAnswer(
  answer: ProtocolDefinitionDto["steps"][number]["occurrences"][number]["parameterValues"][number],
): ProtocolBuilderDomainAnswer {
  return {
    source: answer.source,
    parameterId: answer.parameterId,
    value:
      answer.value.kind === "Choice" ? { ...answer.value, selected: [...answer.value.selected] } : { ...answer.value },
  };
}

export interface RedesignedProtocolContent {
  envelopeMinutes: number;
  steps: Array<{
    placement: { offsetMinutes: number; durationMinutes?: number };
    title?: string;
    occurrences: Array<{
      id?: string;
      requiredBy?: string;
      definition: string;
      qualifier?: string;
      parameterValues: ProtocolBuilderDomainAnswer[];
      plannedTrials?: ProtocolBuilderPlannedTrial[];
      observationLevels?: { observationId: string; level: "trial" }[];
    }>;
  }>;
  exams: string[];
}

/** Builder form -> domain command facts. FHIR/UI-only nesting stops here. */
export function buildRedesignedProtocolContent(values: PlanDefinitionFormValues): RedesignedProtocolContent {
  const sourceSteps = values.steps;
  const steps = sourceSteps.flatMap((step) => {
    const occurrences = step.actions.flatMap((action) => {
      if (!action.definitionCanonical) return [];
      return [
        {
          ...(memberIdSchema.safeParse(action.id).success ? { id: action.id } : {}),
          definition: action.definitionCanonical,
          ...(action.qualifier?.trim() ? { qualifier: action.qualifier.trim() } : {}),
          parameterValues: action.parameterValues.filter(
            (answer): answer is ProtocolBuilderDomainAnswer => !("name" in answer),
          ),
          ...(action.observationLevels?.length ? { observationLevels: action.observationLevels } : {}),
          ...(action.plannedTrials?.length ? { plannedTrials: action.plannedTrials } : {}),
        },
      ];
    });
    return occurrences.length > 0
      ? [
          {
            placement: {
              offsetMinutes: step.offsetMinutes,
              ...(step.durationMinutes === undefined ? {} : { durationMinutes: step.durationMinutes }),
            },
            ...(step.title.trim() ? { title: step.title.trim() } : {}),
            occurrences,
          },
        ]
      : [];
  });
  return {
    envelopeMinutes: values.envelopeMinutes,
    steps,
    exams: [...new Set(values.exams.map((exam) => exam.canonical))],
  };
}

export function useProtocolDefinitionBuilderForm(
  definition: ProtocolDefinitionDto | undefined,
  exams: readonly ExamDefinitionDto[],
) {
  const defaultValues = useMemo(() => protocolDefinitionToBuilderDefaults(definition, exams), [definition, exams]);
  return useAppForm({
    defaultValues,
    validators: { onDynamic: planDefinitionFormSchema },
    onSubmit: () => {},
  });
}

export type PlanDefinitionForm = ReturnType<typeof usePlanDefinitionForm>;

export { withForm } from "@/shared/forms/app-form";

/** Resolve authored extent without changing the authored duration or rounding draft values. */
export function resolveBuilderStep(step: StepFormValues) {
  return resolvePlacement(step);
}

/** New work starts after the latest included step; existing placements stay put. */
export function nextStepOffset(values: PlanDefinitionFormValues): number {
  return Math.max(
    0,
    ...buildRedesignedProtocolContent(values).steps.map((step) => resolvePlacement(step.placement).endMinutes),
  );
}
